// deps
import React, { useState, ReactElement, useCallback, memo } from "react";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";

// internal deps
import { NewTag, useEffectOnce } from "@swiggy-private/core-ui";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// styles
import { StyledTooltip, TooltipIcon, TooltipContainer, TooltipText, NewTagWrapper, TooltipContent } from "./styles";

// test id
import { TOOLTIP_TEST_ID, TOOLTIP_CLOSE_ICON_TEST_ID } from "@swiggy-private/payment-test-ids";

import { IColorMode } from "@swiggy-private/dls-web-common/dist/components/types";

export interface TooltipProps {
    bottomArrowPosition?: "left" | "right";
    onTooltipClose?: () => void;
    onTooltipVisible?: () => void;
    tooltipDetails?: {
        text?: string;
        enabled?: boolean;
        key?: string;
        icon?: string;
        newTag?: boolean;
    };
    forceColorMode?: IColorMode;
    size?: "default" | "large";
}

function TurboTooltip({
    bottomArrowPosition = "right",
    onTooltipClose,
    onTooltipVisible,
    tooltipDetails,
    forceColorMode,
    size = "default",
}: TooltipProps): ReactElement | null {
    const [showTooltip, setShowTooltip] = useState(true);

    const onCloseTooltip = useCallback(() => {
        // write close click event here
        onTooltipClose && onTooltipClose();
        setShowTooltip(false);
    }, [onTooltipClose]);

    useEffectOnce(() => {
        onTooltipVisible && onTooltipVisible();
    });

    if (!showTooltip) {
        return null;
    }

    return (
        <TooltipContainer as="div" bottomArrowPosition={bottomArrowPosition} data-testid={TOOLTIP_TEST_ID}>
            <StyledTooltip
                bottomArrowPosition={bottomArrowPosition}
                closeIconDataTestId={TOOLTIP_CLOSE_ICON_TEST_ID}
                onClickClose={onCloseTooltip}
                forceColorMode={forceColorMode}
                size={size}
            >
                {tooltipDetails?.newTag && (
                    <NewTagWrapper>
                        <NewTag />
                    </NewTagWrapper>
                )}
                <TooltipContent>
                    {!isEmpty(tooltipDetails?.icon) && (
                        <TooltipIcon size={size}>
                            <Image
                                alt={tooltipDetails?.icon || ""}
                                cloudinaryId={tooltipDetails?.icon}
                                height="100%"
                                width="100%"
                            />
                        </TooltipIcon>
                    )}
                    <TooltipText maxLines={2} variant="V2.body2.neutral.medium" color="text_inverse_Highest_Emphasis">
                        {tooltipDetails?.text}
                    </TooltipText>
                </TooltipContent>
            </StyledTooltip>
        </TooltipContainer>
    );
}

export default memo(TurboTooltip);
